/**
 * The period a report covers. The generator only knows calendar months
 * (`POST /generate` takes `{ year, month }`), so the picker offers months —
 * an arbitrary date range is a later phase of the project.
 */
export interface ReportPeriod {
  year: number;
  /** 1-12, matching the API. Not the 0-based month of `Date`. */
  month: number;
  /** "February, 2026" — same wording as the Date column. */
  label: string;
}

/** How many months back the picker offers by default. */
export const DEFAULT_PERIOD_COUNT = 12;

function formatPeriodLabel(year: number, month: number): string {
  const date = new Date(Date.UTC(year, month - 1, 1));
  const monthName = date.toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${monthName}, ${year}`;
}

/**
 * The most recent **complete** months, newest first.
 *
 * The current month is deliberately excluded: its data is still accruing, so a
 * report for it would understate every total and could never be regenerated to
 * a different answer without confusing whoever downloaded the first one.
 *
 * `reference` is injected so the list is deterministic under test.
 */
export function buildRecentPeriods(
  reference: Date,
  count: number = DEFAULT_PERIOD_COUNT
): ReportPeriod[] {
  const periods: ReportPeriod[] = [];
  // Start from the month before `reference`, in UTC to match the API's months.
  let year = reference.getUTCFullYear();
  let month = reference.getUTCMonth(); // 0-based "now" === 1-based previous month

  for (let i = 0; i < count; i += 1) {
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    periods.push({ year, month, label: formatPeriodLabel(year, month) });
    month -= 1;
  }

  return periods;
}

/** Find the period a label came from — the dropdown hands back its string. */
export function findPeriodByLabel(
  periods: ReportPeriod[],
  label: string
): ReportPeriod | null {
  return periods.find((period) => period.label === label) ?? null;
}
